import { useParams } from 'react-router-dom'
import { useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useStore } from 'react-redux'
import { AuthContext } from '@/context/auth-context'
import { useEditingLease } from '@/editing-leases/hooks/useEditingLease'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { canEditTimeline } from '@/matter/utils/matter-permissions'
import { selectMatterById } from '@/matter/store/selectors'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { TimelineEditingLeaseContext } from '../hooks/useTimelineEditingLease'
import { requestEditingLease } from '../fetch/editing-lease'
import { loadTimelineWorkspace } from '../store/load-workspace'
import { addTimelineAssetListener } from '../store/listeners'
import { TimelineAssetActions } from '../store/slice'
import { EditingResourceKind } from '@/editing-leases/schemas'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'

export default function TimelineEditingLeaseProvider({ children }: { children: ReactNode }) {
  const { user } = useContext(AuthContext)
  const { matterId } = useParams<{ matterId: string }>()
  return (
    <WorkspaceLease key={`${user?.uid}:${matterId}`} matterId={matterId} userId={user?.uid}>
      {children}
    </WorkspaceLease>
  )
}

function WorkspaceLease({ children, matterId, userId }: { children: ReactNode; matterId?: string; userId?: string }) {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const matter = useAppSelector((state) => selectMatterById(state, { matterId }))
  const editor = useRuntimeFeatureFlag(RuntimeFeatureFlag.COLLAB_EDITOR_ROLE)
  const api = useMemo(
    () => ({
      status: () => requestEditingLease(matterId!, 'status'),
      acquire: (age?: number) => requestEditingLease(matterId!, 'acquire', age),
      renew: (age?: number) => requestEditingLease(matterId!, 'renew', age),
    }),
    [matterId]
  )
  const refresh = useCallback(
    (isCurrent: () => boolean) => loadTimelineWorkspace({ matterId: matterId!, force: true }, { dispatch, getState: store.getState, isCurrent }),
    [matterId, dispatch, store]
  )
  const onStartEditingFailure = useCallback(
    () =>
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Something went wrong. Try refreshing the page.', durationMs: kToastDurationMediumMs })),
    [dispatch]
  )
  const lease = useEditingLease({
    resource: { kind: EditingResourceKind.TIMELINE, resource_id: matterId ?? '', matter_id: matterId ?? '' },
    eligible: canEditTimeline(matter, editor.enabled && !editor.loading),
    ready: Boolean(userId && matterId && !editor.loading),
    enabled: editor.enabled,
    api,
    refresh,
    onStartEditingFailure,
  })
  const { handleDenial } = lease
  useEffect(
    () =>
      dispatch(
        addTimelineAssetListener({
          actionCreator: TimelineAssetActions.editingLeaseRejected,
          effect: (action) => {
            handleDenial(action.payload)
          },
        })
      ),
    [dispatch, handleDenial]
  )
  return (
    <TimelineEditingLeaseContext.Provider value={lease}>
      <div
        className="flex flex-col min-h-0 h-full w-full"
        onWheelCapture={lease.recordActivity}
        onTouchMoveCapture={lease.recordActivity}
        onKeyDownCapture={lease.recordActivity}
        onPointerDownCapture={lease.recordActivity}
      >
        {children}
      </div>
    </TimelineEditingLeaseContext.Provider>
  )
}
